import { createSelector } from 'reselect';
import { SummaryProductContextService } from '../../../../../../shared/SummaryProductContext.service';
import {
  ALL_DATA_VALUE,
  OLD_SUMMARY_TILE_CONTEXT_KEY,
  QUICK_LINKS_VALUE,
  UNIVERSAL_EMAIL_VALUE,
} from '../../../../../../shared/constants/summary';
import type { ProductType } from '../../../../../../shared/types/summary.types';
import { SUMMARY_TILE } from '../../../../../../shared/constants/analytics/contexts';
import { SUMMARY_PAGE_PRODUCT_TABS_V2 } from '../../../../../../shared/constants/flipperFlags';
import { isFeatureEnabled } from '../../../../redux/domains/FlipperUpdates/Flipper.selectors';
import EmailsTimeSeriesService from '../../ScreenEmail/Analytics/EmailsTimeSeries/EmailsTimeSeries.service';
import {
  INIT_DEFAULT_RANGE,
  QUICK_LINKS_FILTER_DIMENSION,
  DEFAULT_DIMENSIONS,
  PRODUCT_REQUIRED_FILTER_MAPPING,
  PRODUCT_SOURCE_QUERY_MAPPING,
  PRODUCT_HAS_ORGANIC_TOGGLE_MAPPING,
  PRODUCT_COMPARE_BY_MAPPING,
  PRODUCT_EVENT_LIST_MAPPING,
  PRODUCT_DATA_SOURCES_TRENDS_MAPPING,
  PRODUCT_DATA_SOURCES_REPORT_MAPPING,
  PRODUCT_REPORT_DEFAULT_DIMENSIONS_MAPPING,
  PRODUCT_TRENDS_DEFAULT_DIMENSIONS_MAPPING,
  PRODUCT_TILES_MAPPING,
  PRODUCT_REPORT_DEFAULT_FILTER_MAPPING,
  PRODUCT_TRENDS_DEFAULT_FILTER_MAPPING,
} from '../newSummaryConstants';
import { NEW_SUMMARY_NAMESPACE } from '../../../../../../shared/constants/shareableReports';
import { handleGetDateRangeInfo } from '../../../support/dateUtils';
import { filterSelectedColumns } from '../../../../common/support/utils/filterSelectedColumns';
import { getRoles as getAccessControlRoles } from '../../../selectors/AccessControlSelectors';
import { getUnarchivedLinks as getUnarchivedQuickLinks } from '../../../selectors/QuickLinksSelectors';
import InFilter from '../../../../common/support/utils/druid/filters/InFilter';
import type { DateRangeInfo } from '../../../types/dateRangeTypes';
import type { SummaryTileConfig } from '../newSummaryTypes';
import type { Filter, SourceQuery } from '../../../types/druidTypes';
import type { SingleNamespaceData, ShareableData } from '../../../../common/types/shareableTypes';
import type { RootState } from 'client/js/redux';

const FALLBACK_PRODUCT = ALL_DATA_VALUE;

export const selectAreSummaryProductTabsV2Enabled = createSelector(
  (state: RootState) => isFeatureEnabled(state, SUMMARY_PAGE_PRODUCT_TABS_V2),
  (areSummaryProductTabsV2Enabled) => areSummaryProductTabsV2Enabled
);

const getShareableState = (state: RootState) => state.shareable;

const getShareableData: (state: RootState) => ShareableData = createSelector(
  getShareableState,
  (shareableState) => shareableState.data
);
const getSummaryState: (state: RootState) => SingleNamespaceData = createSelector(
  getShareableData,
  (shareableData) => shareableData[NEW_SUMMARY_NAMESPACE] || {}
);

export const getDateRangeInfo: (state: RootState) => DateRangeInfo = createSelector(getSummaryState, (summaryState) => {
  const { dateRange, defaultRange } = summaryState;
  const dateRangeInfo: DateRangeInfo = {
    dateRange,
    defaultRange,
  };
  return handleGetDateRangeInfo(dateRangeInfo, INIT_DEFAULT_RANGE);
});
export const getSelectedProduct: (state: RootState) => ProductType = createSelector(
  getSummaryState,
  (summaryState) => summaryState.selectedProduct || FALLBACK_PRODUCT
);
export const getRequiredFilter: (state: RootState) => Filter = createSelector(
  getSelectedProduct,
  getUnarchivedQuickLinks,
  (selectedProduct, links) => {
    if (selectedProduct === QUICK_LINKS_VALUE) {
      const quickLinksRequiredFilter = new InFilter(
        QUICK_LINKS_FILTER_DIMENSION,
        links.map((link) => link.id)
      );
      return links.length ? quickLinksRequiredFilter : {};
    }

    return PRODUCT_REQUIRED_FILTER_MAPPING[selectedProduct];
  }
);
export const selectHasOrganicToggle: (state: RootState) => boolean = createSelector(
  getSelectedProduct,
  (selectedProduct) => {
    return PRODUCT_HAS_ORGANIC_TOGGLE_MAPPING[selectedProduct];
  }
);
export const getSourceQuery: (state: RootState) => SourceQuery = createSelector(
  getSelectedProduct,
  (selectedProduct) => PRODUCT_SOURCE_QUERY_MAPPING[selectedProduct]
);

export const getTileConfig: (state: RootState) => SummaryTileConfig = createSelector(
  getSummaryState,
  getSelectedProduct,
  selectAreSummaryProductTabsV2Enabled,
  (summaryState, selectedProduct, productTabsV2Enabled) => {
    const context = productTabsV2Enabled
      ? SummaryProductContextService.encodeContext(SUMMARY_TILE, selectedProduct)
      : OLD_SUMMARY_TILE_CONTEXT_KEY;

    return summaryState[context] || PRODUCT_TILES_MAPPING[productTabsV2Enabled ? selectedProduct : FALLBACK_PRODUCT];
  }
);

// NOTE: This selector is only used in the old SummarySmartTable__LEGACY component.
// identical to getSummaryState except the revenue column within selectedColumns gets filtered
// out depending on access resource permissions (see filterSelectedColumns in common/support/utils)
export const getSmartTablePageState: (state: RootState) => SingleNamespaceData = createSelector(
  getSummaryState,
  getAccessControlRoles,
  (summaryState, accessControlRoles) => filterSelectedColumns(summaryState, accessControlRoles)
);
// NOTE: This selector is only used in the old SummarySmartTable__LEGACY component.
export const getTableQueryDimensions: (state: RootState) => string[] = createSelector(getSummaryState, (summaryState) => {
  const tableState = summaryState['summary-table'];
  const tableQuery = (tableState && tableState.query) || {};
  return tableQuery.dimensions || DEFAULT_DIMENSIONS;
});

export const selectCompareBy = createSelector(
  getSelectedProduct,
  selectAreSummaryProductTabsV2Enabled,
  (selectedProduct, productTabsV2Enabled) => {
    return PRODUCT_COMPARE_BY_MAPPING[productTabsV2Enabled ? selectedProduct : FALLBACK_PRODUCT];
  }
);

export const selectEventList = createSelector(
  getSelectedProduct,
  selectAreSummaryProductTabsV2Enabled,
  (selectedProduct, productTabsV2Enabled) => {
    return PRODUCT_EVENT_LIST_MAPPING[productTabsV2Enabled ? selectedProduct : FALLBACK_PRODUCT];
  }
);

export const selectReportsDataSources = createSelector(
  getSelectedProduct,
  selectAreSummaryProductTabsV2Enabled,
  (selectedProduct, productTabsV2Enabled) => {
    return PRODUCT_DATA_SOURCES_REPORT_MAPPING[productTabsV2Enabled ? selectedProduct : FALLBACK_PRODUCT];
  }
);

export const selectTrendsDataSources = createSelector(
  getSelectedProduct,
  (state: RootState) => state.emailOnboarding?.upsell?.hasRevenueEvents,
  selectAreSummaryProductTabsV2Enabled,
  (selectedProduct, hasRevenueEvents, productTabsV2Enabled) => {
    if (selectedProduct === UNIVERSAL_EMAIL_VALUE) {
      return EmailsTimeSeriesService.getDefaultSummaryDataSources(hasRevenueEvents);
    }

    return PRODUCT_DATA_SOURCES_TRENDS_MAPPING[productTabsV2Enabled ? selectedProduct : FALLBACK_PRODUCT];
  }
);

export const selectDefaultReportDimensions = createSelector(
  getSelectedProduct,
  selectAreSummaryProductTabsV2Enabled,
  (selectedProduct, productTabsV2Enabled) => {
    return PRODUCT_REPORT_DEFAULT_DIMENSIONS_MAPPING[productTabsV2Enabled ? selectedProduct : FALLBACK_PRODUCT];
  }
);

export const selectDefaultTrendsDimensions = createSelector(
  getSelectedProduct,
  selectAreSummaryProductTabsV2Enabled,
  (selectedProduct, productTabsV2Enabled) => {
    return PRODUCT_TRENDS_DEFAULT_DIMENSIONS_MAPPING[productTabsV2Enabled ? selectedProduct : FALLBACK_PRODUCT];
  }
);

export const selectReportDefaultFilters = createSelector(
  getSelectedProduct,
  selectAreSummaryProductTabsV2Enabled,
  (selectedProduct, productTabsV2Enabled) => {
    return PRODUCT_REPORT_DEFAULT_FILTER_MAPPING[productTabsV2Enabled ? selectedProduct : FALLBACK_PRODUCT];
  }
);

export const selectTrendsDefaultFilters = createSelector(
  getSelectedProduct,
  selectAreSummaryProductTabsV2Enabled,
  (selectedProduct, productTabsV2Enabled) => {
    return PRODUCT_TRENDS_DEFAULT_FILTER_MAPPING[productTabsV2Enabled ? selectedProduct : FALLBACK_PRODUCT];
  }
);
